import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { SecondaryButton, IconButton } from "@/components/StandardButton";
import { useSidebar } from "@/components/ui/sidebar";

const navigationItems = [
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Overview, metrics, and recent activity",
    url: "/dashboard",
    keywords: ["home", "overview", "main"],
  },
  {
    id: "feedback",
    title: "Feedback",
    description: "Inbox, kanban and submissions",
    url: "/dashboard/feedback",
    keywords: ["submissions", "crm", "kanban", "inbox"],
  },
  {
    id: "feedback-pages",
    title: "Feedback Pages",
    description: "Create and manage collection links",
    url: "/dashboard/pages",
    keywords: ["pages", "forms", "links"],
  },
  {
    id: "analytics",
    title: "Analytics",
    description: "Detailed analytics and trends",
    url: "/dashboard/analytics",
    keywords: ["stats", "metrics", "data", "insights"],
  },
  {
    id: "reports",
    title: "Reports",
    description: "Weekly digests and PDF exports",
    url: "/dashboard/reports",
    keywords: ["pdf", "digest", "weekly", "executive"],
  },
  {
    id: "ai-advisor",
    title: "AI Advisor",
    description: "Ask AI about your feedback",
    url: "/dashboard/hr-advisor",
    keywords: ["ai", "chat", "ask", "advisor", "insights"],
  },
  {
    id: "campaigns",
    title: "Campaigns",
    description: "Targeted feedback campaigns",
    url: "/dashboard/campaigns",
    keywords: ["campaign", "outreach", "pulse"],
  },
  {
    id: "manager-view",
    title: "Manager View",
    description: "Department-scoped feedback view",
    url: "/dashboard/manager",
    keywords: ["manager", "department", "team"],
  },
  {
    id: "create-page",
    title: "Create Page",
    description: "New feedback page from template",
    url: "/dashboard/new",
    keywords: ["new", "add", "create", "template"],
  },
];

const settingsItems = [
  {
    id: "general-settings",
    title: "General Settings",
    description: "Update your company name and account information",
    url: "/dashboard/settings?tab=general",
    keywords: ["company", "account", "profile"],
  },
  {
    id: "branding-settings",
    title: "Branding Settings",
    description: "Customize your brand colors and logo",
    url: "/dashboard/settings?tab=branding",
    keywords: ["colors", "logo", "theme", "appearance"],
  },
  {
    id: "team-settings",
    title: "Team Settings",
    description: "Manage team members and invitations",
    url: "/dashboard/settings?tab=team",
    keywords: ["members", "invite", "users", "collaboration"],
  },
  {
    id: "notifications-settings",
    title: "Notification Settings",
    description: "Configure email alerts and notifications",
    url: "/dashboard/settings?tab=notifications",
    keywords: ["email", "alerts", "digest"],
  },
  {
    id: "billing-settings",
    title: "Billing Settings",
    description: "Manage your subscription and usage",
    url: "/dashboard/settings?tab=billing",
    keywords: ["plan", "subscription", "upgrade", "payment"],
  },
  {
    id: "privacy-settings",
    title: "Privacy Settings",
    description: "Data retention and account management",
    url: "/dashboard/settings?tab=privacy",
    keywords: ["data", "export", "delete", "gdpr"],
  },
];

export function SidebarSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { open: sidebarOpen } = useSidebar();
  const { theme, setTheme } = useTheme();

  // Fetch user's feedback pages
  const { data: pages } = useQuery({
    queryKey: ["feedback-pages-search", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback_pages")
        .select("id, title, slug")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = (url: string) => {
    setOpen(false);
    navigate(url);
  };

  const handleAction = async (action: () => Promise<void> | void) => {
    setOpen(false);
    try {
      await action();
    } catch (err) {
      console.error("Command action failed", err);
    }
  };

  const handleLoadSampleData = async () => {
    const { data, error } = await supabase.rpc("seed_demo_data");
    if (error) {
      toast.error("Could not load sample data", { description: error.message });
      return;
    }
    const result = data as { success?: boolean; already_existed?: boolean; error?: string } | null;
    if (!result?.success) {
      toast.error(result?.error ?? "Could not load sample data");
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["feedback-pages"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] }),
      queryClient.invalidateQueries({ queryKey: ["feedback-pages-search"] }),
    ]);
    toast.success(
      result.already_existed ? "Sample data already loaded" : "Sample data added to your workspace"
    );
    navigate("/dashboard");
  };

  const handleToggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
    toast.success(`Switched to ${theme === "dark" ? "light" : "dark"} mode`);
  };

  return (
    <>
      {sidebarOpen ? (
        <SecondaryButton
          className="w-full justify-start text-muted-foreground hover:text-foreground bg-background border-border"
          onClick={() => setOpen(true)}
        >
          <Search className="mr-2 h-4 w-4" strokeWidth={1.75} />
          <span className="flex-1 text-left">Search...</span>
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
            <span className="text-xs">⌘</span>K
          </kbd>
        </SecondaryButton>
      ) : (
        <IconButton
          className="w-full h-10 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-lg transition-all duration-200"
          onClick={() => setOpen(true)}
          title="Search (⌘K)"
        >
          <Search className="h-5 w-5" strokeWidth={1.75} />
        </IconButton>
      )}

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search navigation, settings, and pages..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          <CommandGroup heading="Quick actions">
            <CommandItem
              onSelect={() => handleAction(handleLoadSampleData)}
              keywords={["demo", "sample", "seed", "example", "test"]}
            >
              <div className="flex flex-col">
                <span className="font-medium">Load sample data</span>
                <span className="text-sm text-muted-foreground">
                  Add a demo page with realistic feedback to explore features
                </span>
              </div>
            </CommandItem>
            <CommandItem
              onSelect={() => handleAction(handleToggleTheme)}
              keywords={["dark", "light", "theme", "mode", "appearance"]}
            >
              <div className="flex flex-col">
                <span className="font-medium">
                  Switch to {theme === "dark" ? "light" : "dark"} mode
                </span>
                <span className="text-sm text-muted-foreground">
                  Toggle the visual theme
                </span>
              </div>
            </CommandItem>
            <CommandItem
              onSelect={() => handleSelect("/dashboard/new")}
              keywords={["template", "new", "create", "gallery"]}
            >
              <div className="flex flex-col">
                <span className="font-medium">Browse page templates</span>
                <span className="text-sm text-muted-foreground">
                  Start from a pre-built feedback template
                </span>
              </div>
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Navigation">
            {navigationItems.map((item) => (
              <CommandItem
                key={item.id}
                onSelect={() => handleSelect(item.url)}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{item.title}</span>
                  <span className="text-sm text-muted-foreground">
                    {item.description}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="Settings">
            {settingsItems.map((item) => (
              <CommandItem
                key={item.id}
                onSelect={() => handleSelect(item.url)}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{item.title}</span>
                  <span className="text-sm text-muted-foreground">
                    {item.description}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>

          {pages && pages.length > 0 && (
            <CommandGroup heading="Your Pages">
              {pages.map((page) => (
                <CommandItem
                  key={page.id}
                  onSelect={() => handleSelect(`/dashboard/pages/${page.id}`)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{page.title}</span>
                    <span className="text-sm text-muted-foreground">
                      /{page.slug}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
